import { Cell, Grid, Typography } from '@pickupp/ui/core'
import { makeStyles, useTheme } from '@pickupp/ui/styles'
import cx from 'classnames'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'

import enAppStoreBadge from '~/src/assets/app-store-badge-en.png'
import msAppStoreBadge from '~/src/assets/app-store-badge-ms.png'
import zhAppStoreBadge from '~/src/assets/app-store-badge-zh.png'
import enGooglePlayBadge from '~/src/assets/google-play-badge-en.png'
import msGooglePlayBadge from '~/src/assets/google-play-badge-ms.png'
import zhGooglePlayBadge from '~/src/assets/google-play-badge-zh.png'
import Icon from '~/src/components/Icon'
import Link from '~/src/components/Link'
import { LocaleContext } from '~/src/contexts'
import { useBrowserSize } from '~/src/hooks'

import styles from './styles'

const useStyles = makeStyles(styles)

const storeLinkBadgeMap = {
  google: {
    en: enGooglePlayBadge,
    zh: zhGooglePlayBadge,
    ms: msGooglePlayBadge,
  },
  apple: {
    en: enAppStoreBadge,
    zh: zhAppStoreBadge,
    ms: msAppStoreBadge,
  },
}

const FooterLogo = ({ logo }) => {
  const theme = useTheme()

  return (
    <Link to="/">
      <img
        loading="lazy"
        src={logo.src?.data?.attributes?.url}
        alt={logo.alt}
        width={theme.global.logoWidth}
      />
    </Link>
  )
}

FooterLogo.propTypes = {
  logo: PropTypes.object.isRequired,
}

const FooterNavigation = ({ columns }) => {
  const classes = useStyles()
  return (
    <Grid
      spacing={2}
      className={classes.navigationContainer}
    >
      {columns?.map((column) => (
        <Cell
          key={column.label}
          className={classes.columnContainer}
          md
        >
          <div>
            {column.link ? (
              <Link
                to={column.link.url}
                target={column.link.target}
                className={cx(
                  classes.link,
                  classes.noUnderline,
                )}
                data-identity={column.link.identity}
                event_name={column.link.event_name}
              >
                <Typography variant="inherit" weight={700}>
                  {column.link.text || column.label}
                </Typography>
              </Link>
            ) : (
              <Typography
                variant="inherit"
                weight={700}
              >
                {column.label}
              </Typography>
            )}
          </div>
          <div className={classes.menuContainer}>
            {column.items?.map((item, index) => (
              <React.Fragment key={index}>
                <div>
                  {item.link ? (
                    <Link
                      key={item.label || item.link.text}
                      to={item.link.url}
                      target={item.link.target}
                      className={classes.link}
                      data-identity={item.link.identity}
                      event_name={item.link.event_name}
                    >
                      <Typography variant="inherit">
                        {item.link.text || item.label}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      key={item.label}
                      variant="inherit"
                    >
                      {item.label}
                    </Typography>
                  )}
                </div>
                {item.items?.length > 0 && (
                  <ul className={classes.subMenuContainer}>
                    {item.items.map((subItem, i) => (
                      subItem.link ? (
                        <li key={i}>
                          <Link
                            key={subItem.label || subItem.link.text}
                            to={subItem.link.url}
                            target={subItem.link.target}
                            className={classes.link}
                            data-identity={subItem.link.identity}
                            event_name={subItem.link.event_name}
                          >
                            <Typography variant="inherit">
                              {subItem.link.text || subItem.label}
                            </Typography>
                          </Link>
                        </li>
                      ) : (
                        <li>
                          <Typography
                            key={subItem.label}
                            variant="inherit"
                          >
                            {subItem.label}
                          </Typography>
                        </li>
                      )))}
                  </ul>
                )}
              </React.Fragment>
            ))}
          </div>
        </Cell>
      ))}
    </Grid>
  )
}

FooterNavigation.propTypes = {
  columns: PropTypes.array.isRequired,
}

const FooterPrimary = ({
  logo,
  columns,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.primaryContainer}>
      <Grid className={classes.primaryContent}>
        <Cell
          xs={12}
          sm={3}
        >
          {logo && (
            <FooterLogo logo={logo} />
          )}
        </Cell>
        <Cell
          xs={12}
        >
          <FooterNavigation columns={columns} />
        </Cell>
      </Grid>
    </div>
  )
}

FooterPrimary.propTypes = {
  logo: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
}

const FooterCopyright = ({ copyRight }) => {
  const classes = useStyles()

  return (
    <div className={classes.copyrightContainer}>
      <Typography variant="body2" html={copyRight} />
    </div>
  )
}

FooterCopyright.propTypes = {
  copyRight: PropTypes.string.isRequired,
}

const FooterStoreLinks = () => {
  const classes = useStyles()
  const { language, region } = useContext(LocaleContext)

  return (
    <div className={classes.storeLinksContainer}>
      <Link
        to="https://play.google.com/store/apps/details?id=com.pickupp.userapp"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          className={classes.storeBadge}
          src={storeLinkBadgeMap.google[language.toLowerCase()]}
          alt="pickupp user app google play link"
        />
      </Link>
      <Link
        to={`https://apps.apple.com/${region.toLowerCase()}/app/pickupp-user/id1433497708`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          className={classes.storeBadge}
          src={storeLinkBadgeMap.apple[language.toLowerCase()]}
          alt="pickupp user app apple store link"
        />
      </Link>
    </div>
  )
}

const FooterSocial = ({ socials }) => {
  const classes = useStyles()

  return (
    <div className={classes.socialContainer}>
      {socials.map(({ link, icon }) => (
        <Link
          key={link.url}
          to={link.url}
          target={link.target}
          rel="noopener noreferrer"
          className={classes.social}
          data-identity={link.identity}
          event_name={link.event_name}
        >
          <Icon icon={icon} />
        </Link>
      ))}
    </div>
  )
}

FooterSocial.propTypes = {
  socials: PropTypes.array.isRequired,
}

const FooterSecondary = ({ copyRight, socials }) => {
  const classes = useStyles()
  const size = useBrowserSize()

  return (
    <div className={classes.secondaryContainer}>
      <Grid className={classes.secondaryContent}>
        <Cell
          sm={12}
          md={6}
          className={classes.footerCopyright}
        >
          <FooterCopyright copyRight={copyRight} />
        </Cell>
        <Cell
          sm={12}
          md={6}
          className={classes.footerSocial}
        >
          {['md', 'lg', 'xl'].includes(size.toLowerCase()) && (
            <FooterStoreLinks />
          )}
          {socials && (
            <FooterSocial socials={socials} />
          )}
        </Cell>
        {!['md', 'lg', 'xl'].includes(size.toLowerCase()) && (
          <Cell sm={12}>
            <FooterStoreLinks />
          </Cell>
        )}
      </Grid>
    </div>
  )
}

FooterSecondary.propTypes = {
  copyRight: PropTypes.string.isRequired,
  socials: PropTypes.array.isRequired,
}

const Footer = ({
  logo,
  columns,
  copyright,
  socials,
}) => {
  return (
    <footer>
      <Grid>
        <Cell xs={12}>
          <FooterPrimary
            logo={logo}
            columns={columns}
          />
        </Cell>
        <Cell xs={12}>
          <FooterSecondary
            copyRight={copyright}
            socials={socials}
          />
        </Cell>
      </Grid>
    </footer>
  )
}

Footer.propTypes = {
  logo: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  copyright: PropTypes.string.isRequired,
  socials: PropTypes.array.isRequired,
}

export default Footer
